/*
 *  Developed by CQ Inversiones SAS. Copyright (c) 2024. All rights reserved.
 *  Desarrollado por CQ Inversiones SAS. Copyright (c) 2024. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez
 * Date:         6/05/24 - 22:37:52
 * Module name:  mkad-table.models
 * File name:    mkad-table.models.ts
 * IDE:          WebStorm
 */


import {ColumnMode, SelectionType, SortType} from '@swimlane/ngx-datatable';
import {TableIconEnum} from '@shared/components/table';
import {NbPosition} from '@nebular/theme/components/cdk/overlay/overlay-position';

/**
 * @description
 * Defines CSS classes for an active or inactive table.
 */
export interface TableCssClassConfigInterface {
  activeCssClass: string;
  inactiveCssClass: string;
}

/**
 * @description
 * Add activeLabel and inactiveLabel to TableCssClassConfigInterface
 */
export interface TableBasicConfigInterface extends TableCssClassConfigInterface {
  activeLabel: string;
  inactiveLabel: string;
}

/**
 * @description
 * Configure the display of an image in a table
 */
export interface TableImageConfigInterface {
  alternativeText?: string;
  noImageText?: string;
  error?: string;
}

/**
 * @description
 * Adds activeIcon and inactiveIcon to TableCssClassConfigInterface.
 */
export interface TableDualIconConfigInterface extends TableCssClassConfigInterface {
  activeIcon: string;
  inactiveIcon: string;
}

/**
 * @description
 * Define the icon and cssClass properties
 */
export interface TableIconConfigInterface {
  icon: string;
  cssClass: string;
}

/**
 * @description
 * Column type support
 * @enum
 */
export enum TableColumnEnum {
  'tag' = 'tag',
  'text' = 'text',
  'phone' = 'phone',
  'textDual' = 'textDual',
  'image' = 'image',
  'enum' = 'enum',
  'icon' = 'icon',
  'iconDual' = 'iconDual',
  'iconEnum' = 'iconEnum',
  'iconGroup' = 'iconGroup',
  'initialsGroup' = 'initialsGroup',
}

/**
 * @description
 * Action type support
 * @enum
 */
export enum TableActionEnum {
  update,
  delete,
  add,
  customBasic,
  customGroups,
  customParams,
  customPorts,
  limits,
  swap,
  userAdd,
  row
}

/**
 * @description
 * Theme table lib support
 * @enum
 */
export enum TableThemeEnum {
  'light' = 'light',
  'dark' = 'dark'
}

/**
 * @description
 * Type for columns align
 * @type
 */
export enum TableColumnAlignEnum {
  'center' = 'center',
  'end' = 'end',
  'start' = 'start'
}

/**
 * @description
 * Defines an object with the initials of a table
 */
export interface TableInitialsInterface {
  initials: string[],
  total: number
}

/**
 * @description
 * Model that returns the click event on the rows
 * @interface
 */
export interface TableRowClickInterface<T> {
  type: TableActionEnum;
  row: T;
}

/**
 * @description
 * Model that returns the click event on the icon
 * @interface
 */
export interface TableIconClickInterface<T = unknown> {
  field: string;
  row: T;
}

/**
 * @description
 * configuration model for button icons
 * @interface
 */
export interface TableIconInterface {
  icon?: string;
  class?: string;
  pack?: 'far' | 'fab';
}

/**
 * @description
 * Configuration model for table columns
 * @interface
 */
export interface TableColumnInterface {
  field: string | string[];
  header: string;
  align?: TableColumnAlignEnum;
  sort?: boolean;
  resize?: boolean;
  grow?: number;
  type: TableColumnEnum;
  dualIcon?: TableIconEnum[];
  dualField?: string[];
  groupIcon?: TableIconEnum[];
  groupIconFields?: string[];
  groupIconTooltips?: string[];
  enumList?: string[];
  dualIconClass?: Record<number, string>;
  dualIconTooltip?: Record<number, string>;
  enumIcon?: Record<string, TableIconEnum>;
  enumIconTooltip?: Record<string, string>;
  tooltipAlign?: NbPosition;
}

/**
 * @description
 * Configuration model for functional buttons
 * @interface
 */
export interface TableActionColumnInterface {
  type: TableActionEnum;
  icon?: string;
  cssClass?: string;
  tooltip?: string;
  disable?:<T = Record<string, string>> (param?: T) => boolean;
}

/**
 * @description
 * Configuration model for action columns
 * @interface
 */
export interface TableActionConfigInterface {
  data: TableActionColumnInterface[];
  header?: string;
  tooltipAlign?: NbPosition;
}

/**
 * @description
 * Configuration model for table messages
 * @interface
 */
export interface TableMessageConfigInterface {
  empty?: string;
  error?: string;
}

/**
 * @description
 * Configuration model for table cell view config
 * @interface
 */
export interface TableTypeConfigInterface {
  image?: TableImageConfigInterface,
  tag?: TableBasicConfigInterface,
  icon?: TableDualIconConfigInterface,
}

/**
 * @description
 * Model for additional table configuration
 * @interface
 */
export interface TableExtraConfigInterface {
  actions?: TableActionConfigInterface;
  messages?: TableMessageConfigInterface;
  config?: TableTypeConfigInterface;
  image?: TableImageConfigInterface;
  tag?: TableBasicConfigInterface;
  check?: TableCssClassConfigInterface;
  sortType?: SortType;
  selection?: SelectionType;
  columnMode?: ColumnMode;
  clickableRow?: boolean;
}

/**
 * @description
 * Model for the configuration of the basic/child table
 * @interface
 */
export interface TableChildrenConfigInterface {
  field?: string;
  columns: TableColumnInterface[];
  extra?: TableExtraConfigInterface;
  limit?: number;
}

/**
 * @description
 * Model for the configuration of the nested table
 * @interface
 */
export interface TableConfigInterface<T = unknown[]> {
  data: T;
  columns: TableColumnInterface[];
  extra?: TableExtraConfigInterface;
  children?: TableChildrenConfigInterface;
}
